import React, { useState, useEffect } from 'react';
import { UsersRound, UserPlus, Trash2, Shield, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { staffAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';

export default function StaffPage() {
    const { isFirmOwner } = useAuth();
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [form, setForm] = useState({ email: '', name: '', password: '', access: 'full' });
    const [adding, setAdding] = useState(false);
    const [savingAccess, setSavingAccess] = useState(null);
    const [toRemove, setToRemove] = useState(null);
    const [removing, setRemoving] = useState(false);

    const fetchStaff = async () => {
        try {
            const res = await staffAPI.list();
            setStaff(res.data.staff || []);
        } catch (err) {
            if (err.response?.status !== 403) toast.error('Failed to load staff');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (isFirmOwner) fetchStaff(); else setLoading(false); }, [isFirmOwner]);

    const handleAdd = async () => {
        if (!form.email.trim() || !form.name.trim() || !form.password) {
            toast.error('Email, name and password are required'); return;
        }
        setAdding(true);
        try {
            await staffAPI.add({ email: form.email.trim(), name: form.name.trim(), password: form.password, access: form.access });
            toast.success(`Staff ${form.email.trim()} added (${form.access})`);
            setAddOpen(false);
            setForm({ email: '', name: '', password: '', access: 'full' });
            fetchStaff();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add staff');
        } finally {
            setAdding(false);
        }
    };

    const handleAccessChange = async (staffId, access) => {
        setSavingAccess(staffId);
        try {
            await staffAPI.setAccess(staffId, access);
            setStaff((prev) => prev.map((s) => (s.id === staffId ? { ...s, staff_access: access } : s)));
            toast.success(`Access set to ${access}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update access');
        } finally {
            setSavingAccess(null);
        }
    };

    const handleRemove = async () => {
        if (!toRemove) return;
        setRemoving(true);
        try {
            await staffAPI.remove(toRemove.id);
            toast.success(`Removed ${toRemove.email}`);
            setToRemove(null);
            fetchStaff();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to remove staff');
        } finally {
            setRemoving(false);
        }
    };

    if (!isFirmOwner) {
        return (
            <div className="space-y-6" data-testid="staff-page">
                <h1 className="font-heading text-3xl font-bold">Staff</h1>
                <Card className="border-dashed border-2">
                    <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                        <Shield className="h-12 w-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">Only the firm owner can manage staff.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="staff-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
                        <UsersRound className="h-7 w-7 text-primary" /> Staff
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Add employees to your firm and set each one's access level. Staff can work on all your client companies.
                    </p>
                </div>
                <Button onClick={() => setAddOpen(true)} data-testid="add-staff-btn">
                    <UserPlus className="h-4 w-4 mr-2" /> Add staff
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Firm staff ({staff.length})</CardTitle>
                    <CardDescription>
                        <span className="font-medium">Full</span>: everything you can do (incl. push to e-conomic & delete vouchers).{' '}
                        <span className="font-medium">Limited</span>: review/edit/approve only — cannot push to e-conomic or delete vouchers.
                    </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : staff.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <UsersRound className="h-10 w-10 text-muted-foreground mb-3" />
                            <p className="text-muted-foreground">No staff yet. Add your first employee.</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Added</TableHead>
                                    <TableHead className="w-44">Access</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {staff.map((s) => (
                                    <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                                        <TableCell className="font-medium">{s.name}</TableCell>
                                        <TableCell className="text-muted-foreground">{s.email}</TableCell>
                                        <TableCell className="text-muted-foreground text-sm">{formatDate(s.created_at)}</TableCell>
                                        <TableCell>
                                            <Select
                                                value={s.staff_access || 'full'}
                                                onValueChange={(v) => handleAccessChange(s.id, v)}
                                                disabled={savingAccess === s.id}
                                            >
                                                <SelectTrigger className="w-40 h-8" data-testid={`access-${s.id}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="full">Full access</SelectItem>
                                                    <SelectItem value="limited">Limited</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                {(s.staff_access || 'full') === 'limited'
                                                    ? <Badge variant="outline"><Shield className="h-3 w-3 mr-1" />Limited</Badge>
                                                    : <Badge variant="secondary"><ShieldCheck className="h-3 w-3 mr-1" />Full</Badge>}
                                                <Button
                                                    variant="ghost" size="icon"
                                                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                    onClick={() => setToRemove(s)} title="Remove staff"
                                                    data-testid={`remove-staff-${s.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Add staff dialog */}
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add staff login</DialogTitle>
                        <DialogDescription>
                            Create an employee login for your firm. They'll get access to all your client companies.
                            You set their password now and share it with them. (Email invites can come later.)
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1">
                            <Label htmlFor="s-name">Name</Label>
                            <Input id="s-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="staff-name" />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="s-email">Email</Label>
                            <Input id="s-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="staff-email" />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="s-pass">Temporary password</Label>
                            <Input id="s-pass" type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="staff-password" />
                        </div>
                        <div className="space-y-1">
                            <Label>Access level</Label>
                            <Select value={form.access} onValueChange={(v) => setForm((f) => ({ ...f, access: v }))}>
                                <SelectTrigger data-testid="staff-access"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="full">Full — everything (default)</SelectItem>
                                    <SelectItem value="limited">Limited — no push / no delete vouchers</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddOpen(false)} disabled={adding}>Cancel</Button>
                        <Button onClick={handleAdd} disabled={adding} data-testid="submit-staff-btn">{adding ? 'Adding…' : 'Add staff'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Remove confirmation */}
            <AlertDialog open={!!toRemove} onOpenChange={(o) => { if (!o) setToRemove(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove this staff member?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {toRemove && (<>This removes <span className="font-medium">{toRemove.name}</span> (<span className="font-mono">{toRemove.email}</span>) and revokes their access to your companies. Their past activity history is kept.</>)}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={(e) => { e.preventDefault(); handleRemove(); }} disabled={removing} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-remove-staff">
                            {removing ? 'Removing…' : 'Remove'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
